/**
 * LabeledBars — a ranked horizontal bar list (rejection reasons, sources).
 *
 * Bars are SVG `rect`s rather than divs with a percentage width, for a specific
 * reason: Tailwind cannot see a class assembled at runtime (`w-[${pct}%]` never
 * reaches the generated CSS), and the alternative is an inline `style` prop,
 * which the design system forbids. An SVG `width` attribute is neither.
 *
 * Bars are scaled against the LARGEST value, not against 100%: a widget where
 * the top reason is 34% would otherwise render every bar in the left third and
 * waste the panel. The printed number is the real share, so nothing is
 * misrepresented — only the ink is rescaled.
 *
 * The hover tooltip's one real job here is the LABEL. Count and percentage are
 * already printed on every row, but the label is truncated to a fixed column —
 * "Goldman Sachs Asset Management" and "Goldman Sachs Group" both render as
 * "Goldman Sachs Asset…" at this width, and a ranked list where two rows read
 * identically is a ranking of nothing. It replaces the `title` attribute that
 * did the same job with a ~1s delay and no theming.
 */

import type { SearchReportBucket } from "@nu-assist/contracts";

import { ChartTooltip } from "./ChartTooltip";
import { pointerSide, useHoverPoint } from "./useHoverPoint";

export interface LabeledBarsProps {
  buckets: readonly SearchReportBucket[];
  /** Shown when there is nothing to rank. */
  emptyMessage: string;
  /** Bar colour utility class (e.g. `fill-chart-neutral`). */
  fillClass?: string;
  testId?: string;
}

const BAR_WIDTH = 200;
const BAR_HEIGHT = 12;

export function LabeledBars({
  buckets,
  emptyMessage,
  fillClass = "fill-chart-neutral",
  testId = "labeled-bars",
}: LabeledBarsProps) {
  const hover = useHoverPoint();

  if (buckets.length === 0) {
    return (
      <p data-testid={`${testId}-empty`} className="text-body text-text-secondary">
        {emptyMessage}
      </p>
    );
  }

  const largest = Math.max(...buckets.map((b) => b.count), 1);

  return (
    // `relative` so the tooltip pins to the whole widget rather than to one row:
    // a bubble anchored inside a 12px-tall row would be taller than its own
    // positioning context and clip against the next bar.
    <ul data-testid={testId} className="relative flex flex-col gap-1.5">
      {buckets.map((bucket, index) => (
        <li
          key={bucket.label}
          // The row carries no ACTION — only a tooltip that un-truncates the
          // label and repeats the count and share already printed on it. Nothing
          // here is reachable by pointer alone, so there is nothing to make
          // focusable and no keyboard equivalent to owe anyone.
          onPointerEnter={(event) => hover.show(index, pointerSide(event))}
          onPointerLeave={hover.hide}
          className="flex items-center gap-2 text-caption text-text-secondary"
        >
          <span className="w-44 shrink-0 truncate text-right">{bucket.label}</span>
          <svg
            viewBox={`0 0 ${BAR_WIDTH} ${BAR_HEIGHT}`}
            height={BAR_HEIGHT}
            role="presentation"
            aria-hidden="true"
            className="min-w-0 flex-1"
            preserveAspectRatio="none"
          >
            <rect
              x={0}
              y={0}
              width={BAR_WIDTH}
              height={BAR_HEIGHT}
              rx={3}
              className="fill-chart-track"
            />
            <rect
              x={0}
              y={0}
              width={Math.max(1, (bucket.count / largest) * BAR_WIDTH)}
              height={BAR_HEIGHT}
              rx={3}
              className={fillClass}
            />
          </svg>
          <strong className="w-16 shrink-0 text-text">{bucket.count}</strong>
          <span className="w-14 shrink-0">{bucket.pct}%</span>
        </li>
      ))}
      {hover.index !== null && buckets[hover.index] ? (
        <ChartTooltip
          testId={`${testId}-tooltip`}
          title={buckets[hover.index].label}
          rows={[
            { key: "count", label: "Candidates", value: String(buckets[hover.index].count) },
            { key: "pct", label: "Share", value: `${buckets[hover.index].pct}%` },
          ]}
          side={hover.side}
        />
      ) : null}
    </ul>
  );
}
